import {Box, Grid, Typography} from '@mui/material';
import {useAppSelector} from '@/redux-store/store';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {findAllEvents} from '@/rest/events/find-all-events';
import InfiniteScroll from 'react-infinite-scroll-component';
import LoadingScreen from '@/pages/loading-screen';
import {EventData, EventFormMode} from '@/types/event';
import DialogForm from '@/components/ui/dialogs/form-dialog';
import ResponsiveFab from '@/components/ui/responsive-fab';
import EventDetailsDialog from './details';
import EventCard from './event-card';
import {fetchNextPage} from '@/rest/utility/fetch-next-page';
import {useSnackbar} from '@/contexts/snackbar';
import {getGroupById} from '@/rest/groups/get-group';
import {useDispatch} from 'react-redux';
import {
  setGroupsInRedux,
  updateGroupInRedux
} from '@/redux-store/slices/groups';
import StateSwitch from '@/components/ui/switches/state-switch';
import RefreshButton from '@/components/ui/buttons/refresh-button';
import {findMyCompanyEvents} from '@/rest/events/find-my-company-events';
import {setEventsInRedux} from '@/redux-store/slices/events';
import {findMyCompanyGroups} from '@/rest/groups/find-my-company-groups';
import {useCompanyContext} from '@/contexts/admin/companies';
import {MatrixRoomProvider} from '@/contexts/matrix/room/provider';
import MatrixUnreadEventsProvider from '@/contexts/matrix/unread-events/provider';
import {eventChangeObservable} from '@/rest/events/change-observable';
import {env} from '@/config';
import FilterButton from '../filter/button';
import TextSearch from '../filter/text-search';
import FilterForm from '../filter';
import FilterChip from '../filter/chip';
import {getActiveFilters, isNotEqualFilter} from '@/services/filter';
import {
  initialState,
  resetFilter,
  updateFilter
} from '@/redux-store/slices/filter';
import {Filter} from '@/types/filter';
import EventForm from '../forms/events';
import {getEventById} from '@/rest/events/get-event';
import {useNavigate} from 'react-router-dom';
import {findTrainerEvents} from '@/rest/events/find-trainer-events';
import {findTrainerGroups} from '@/rest/groups/find-trainer-groups';
import {
  resetCurrentEvent,
  resetCurrentGroup,
  resetCurrentOnDemandEvent
} from '@/redux-store/slices/auth';
import {isOneOfUserCompaniesDocument, isPublicCompanyDocument} from '@/utils';
import RemoveFilterButton from '../ui/buttons/remove-filter-button';
interface EventViewProps {
  eventId?: string;
  groupId?: string;
  onDemand?: boolean;
}

const EventView: React.FC<EventViewProps> = ({eventId, groupId, onDemand}) => {
  const {t} = useTranslation();
  const {showSnackbarMessage} = useSnackbar();

  const {APP_VARIANT} = env;

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [events, setEvents] = useState<EventData[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState(true);
  const [showCompanyEvents, setShowCompanyEvents] = useState(false);
  const [prevShowCompanyEvents, setPrevShowCompanyEvents] = useState(true);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);

  const {appTag, isAuthenticated, locationSet, isPublicCompany} =
    useAppSelector((state) => state.auth);
  const userInfos = useAppSelector((state) => state.userInfos.userInfos);
  const myEvents = useAppSelector((state) => state.events.myEvents);
  const myGroups = useAppSelector((state) => state.groups.myGroups);
  const {currentCompanyId} = useAppSelector((state) => state.auth);
  const filter = useAppSelector((state) => state.filter);
  const {isUserHealthManager} = useAppSelector((state) => state.auth);
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);
  const isAdmin = userRole && userRole === 'ADMIN';

  const userId = userInfos._id;

  const isUserTrainer = userRole === 'TRAINER';

  const prevIsAuthenticatedRef = useRef(isAuthenticated);
  const eventFilter = filter[onDemand ? 'onDemandEvent' : 'event'];
  const initialFilterState = initialState[onDemand ? 'onDemandEvent' : 'event'];
  const activeFilters = getActiveFilters(
    eventFilter,
    initialFilterState,
    onDemand ? 'onDemandEvent' : 'event'
  );

  const prevLocationSetRef = useRef(locationSet);

  const {refreshCompanyData} = useCompanyContext() || {};

  const companyId =
    APP_VARIANT === 'sjone' &&
    ((userInfos &&
      userInfos.companies &&
      userInfos.companies.length === 1 &&
      userInfos.companies[0].id._id) ||
      currentCompanyId);

  const fetchNextPageCallback = useCallback(async () => {
    if (!showCompanyEvents) {
      const selectedFilter = filter[onDemand ? 'onDemandEvent' : 'event'];
      const fetchResult = await fetchNextPage(
        page,
        findAllEvents,
        20,
        selectedFilter,
        companyId || undefined,
        userId,
        appTag || undefined,
        onDemand,
        isPublicCompany && !isAuthenticated
      );
      if (fetchResult !== undefined) {
        const {data, nextPage} = fetchResult;

        setPage(nextPage);
        setEvents([...events, ...data]);
        setHasMore(data.length === 20);
      }
    }
  }, [
    showCompanyEvents,
    filter,
    page,
    companyId,
    events,
    appTag,
    onDemand,
    userId,
    isPublicCompany,
    isAuthenticated
  ]);

  const reloadEvents = useCallback(
    (filterData?: Filter) => {
      if (!showCompanyEvents) {
        const selectedFilter = filter[onDemand ? 'onDemandEvent' : 'event'];
        fetchNextPage(
          0,
          findAllEvents,
          20,
          filterData || selectedFilter,
          companyId || undefined,
          userId,
          appTag || undefined,
          onDemand,
          isPublicCompany && !isAuthenticated
        )
          .then((fetchResult) => {
            if (events.length % 20 === 0 && page > 1) {
              setPage(0);
              setHasMore(true);
              setEvents([]);
              fetchNextPageCallback();
            } else if (fetchResult !== undefined) {
              const {data, nextPage} = fetchResult;
              setPage(nextPage);
              setEvents([...data]);
              setHasMore(data.length === 20);
            }
          })

          .catch(console.warn);
      }
    },
    [
      showCompanyEvents,
      events.length,
      page,
      fetchNextPageCallback,
      companyId,
      filter,
      appTag,
      onDemand,
      userId,
      isPublicCompany,
      isAuthenticated
    ]
  );

  const reloadTrainerEvents = useCallback(
    async (filterData?: Filter) => {
      const selectedFilter = filter[onDemand ? 'onDemandEvent' : 'event'];
      const fetchedEvents = await findTrainerEvents(
        filterData || selectedFilter
      );

      dispatch(setEventsInRedux(fetchedEvents));
    },
    [dispatch, filter, onDemand]
  );

  const reloadEventsBasedOnUserRole = useCallback(
    (filterData?: Filter) => {
      if (isUserTrainer && APP_VARIANT === 'sjone') {
        reloadTrainerEvents(filterData);
        setHasMore(false);
      } else {
        reloadEvents(filterData);
      }
    },
    [isUserTrainer, APP_VARIANT, reloadTrainerEvents, reloadEvents]
  );

  const reloadCompanyEvents = useCallback(
    async (filterData?: Filter) => {
      const selectedFilter = filter[onDemand ? 'onDemandEvent' : 'event'];
      const fetchedEvents = await findMyCompanyEvents(
        filterData || selectedFilter
      );
      dispatch(setEventsInRedux(fetchedEvents));
    },
    [dispatch, filter, onDemand]
  );

  const handleShowCompanyEventsChange = (
    event: React.SyntheticEvent<HTMLElement | HTMLInputElement>,
    newValue: boolean
  ) => {
    setShowCompanyEvents(newValue);
    if (newValue) {
      setEvents([...myEvents]);
    } else {
      setEvents([]);
      setPage(0);
      setHasMore(true);
    }
  };

  const onEventChanged = useCallback(
    (newEventData: EventData) => {
      const oldEventIndex = events.findIndex((e) => e._id === newEventData._id);
      if (oldEventIndex >= 0) {
        const newEventsArray = [...events];
        newEventsArray.splice(oldEventIndex, 1, newEventData);
        setEvents(newEventsArray);
      }
    },
    [events]
  );

  const onEventDeleted = useCallback(
    async (deletedEventId: string) => {
      setSelectedEvent(null);
      reloadEventsBasedOnUserRole();

      for (const group of myGroups) {
        if (group.events.some((event) => event._id === deletedEventId)) {
          const updatedGroup = await getGroupById(group._id);

          if (updatedGroup) {
            dispatch(updateGroupInRedux(updatedGroup));
          }

          break;
        }
      }
    },
    [reloadEventsBasedOnUserRole, myGroups, dispatch]
  );

  const handleFabClick = useCallback(() => {
    setShowCreateForm(true);
  }, []);

  const handleEventCreationCancel = useCallback(() => {
    setShowCreateForm(false);
  }, []);

  const onEventCreated = useCallback(async () => {
    setShowCreateForm(false);

    refreshCompanyData();
    showSnackbarMessage(t('event_create_success'), 'success');
    reloadEventsBasedOnUserRole();
  }, [showSnackbarMessage, t, refreshCompanyData, reloadEventsBasedOnUserRole]);

  const onEventCreationError = useCallback(() => {
    showSnackbarMessage(t('event_create_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const onEventCardClick = useCallback((event: EventData) => {
    setSelectedEvent(event);

    setDialogOpen(true);
  }, []);

  const handleCloseEventDetails = useCallback(() => {
    setDialogOpen(false);
    setSelectedEvent(null);

    if (!onDemand) {
      dispatch(resetCurrentEvent());
    } else if (onDemand) {
      dispatch(resetCurrentOnDemandEvent());
    }
    if (eventId) {
      if (appTag) {
        navigate(`/${appTag}`);
      } else {
        navigate('/');
      }
    }
  }, [eventId, dispatch, appTag, onDemand, navigate]);

  const onGroupClose = useCallback(() => {
    if (groupId) {
      dispatch(resetCurrentGroup());
      if (appTag) {
        navigate(`/${appTag}`);
      } else {
        navigate('/');
      }
    }
  }, [appTag, dispatch, groupId, navigate]);

  const handleGroupOfEventChanged = useCallback(async () => {
    try {
      if (APP_VARIANT === 'sjone') {
        let updatedGroupsArray;
        if (isUserHealthManager) {
          updatedGroupsArray = await findMyCompanyGroups();
        } else if (isUserTrainer) {
          updatedGroupsArray = await findTrainerGroups();
        }
        if (updatedGroupsArray && updatedGroupsArray.length > 0) {
          dispatch(setGroupsInRedux(updatedGroupsArray));
        }
      }
    } catch (error) {
      console.error('faled to handle changing of group of event', error);
      throw error;
    }
  }, [APP_VARIANT, dispatch, isUserHealthManager, isUserTrainer]);

  const handleToggleFilterVisibility = useCallback(() => {
    setFilterVisible((prev) => !prev);
  }, []);

  const handleApplyFilter = useCallback(
    (hideFilter?: boolean, filterData?: Filter) => {
      if (hideFilter) {
        setFilterVisible(false);
      }
      if (showCompanyEvents) {
        reloadCompanyEvents(filterData);
      } else {
        reloadEventsBasedOnUserRole(filterData);
      }
    },
    [reloadCompanyEvents, reloadEventsBasedOnUserRole, showCompanyEvents]
  );

  const updateFilterData = useCallback(
    (data: Partial<Filter>) => {
      dispatch(
        updateFilter({type: onDemand ? 'onDemandEvent' : 'event', data})
      );
    },
    [dispatch, onDemand]
  );

  const handleRemoveFilter = useCallback(
    (filterKey: keyof Filter) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newFilterData: any = {
        ...filter[onDemand ? 'onDemandEvent' : 'event']
      };

      const value = initialFilterState[filterKey];

      newFilterData[filterKey] = value as Filter[keyof Filter];

      updateFilterData(newFilterData);
      handleApplyFilter(true, newFilterData);
    },
    [filter, initialFilterState, updateFilterData, handleApplyFilter, onDemand]
  );

  const handleResetFilter = useCallback(() => {
    dispatch(resetFilter({type: onDemand ? 'onDemandEvent' : 'event'}));
    setFilterResetKey((prevKey) => prevKey + 1);
    handleApplyFilter(
      false,
      initialState[onDemand ? 'onDemandEvent' : 'event']
    );
  }, [dispatch, handleApplyFilter, onDemand]);

  const globalFilterChanged = useMemo(() => {
    return isNotEqualFilter(eventFilter, initialFilterState);
  }, [eventFilter, initialFilterState]);

  useEffect(() => {
    if (showCompanyEvents !== prevShowCompanyEvents) {
      setPrevShowCompanyEvents(showCompanyEvents);
      if (showCompanyEvents) {
        setHasMore(false);
      } else {
        reloadEventsBasedOnUserRole();
      }
    }

    const subscription = eventChangeObservable.subscribe({
      next: (changeEvent) => {
        if (changeEvent.type === 'change') {
          const found = events.findIndex((e) => e._id === changeEvent.data._id);
          if (found >= 0) {
            // we have to update an event in our list!
            const newEvents = [...events];
            newEvents.splice(found, 1, changeEvent.data);
            setEvents(newEvents);
          }
        } else if (changeEvent.type === 'new') {
          setEvents((prevEvents) => [changeEvent.data, ...prevEvents]);
        } else if (changeEvent.type === 'deleted') {
          setEvents((prevEvents) =>
            prevEvents.filter((event) => event._id !== changeEvent.id)
          );
        }
      }
    });

    if (showCompanyEvents || (isUserTrainer && APP_VARIANT === 'sjone')) {
      setEvents(myEvents);
    }
    return () => {
      subscription.unsubscribe();
    };
  }, [
    showCompanyEvents,
    prevShowCompanyEvents,
    reloadEventsBasedOnUserRole,
    myEvents,
    events,
    APP_VARIANT,
    isUserTrainer
  ]);

  useEffect(() => {
    if (
      APP_VARIANT !== 'sjone' &&
      !prevIsAuthenticatedRef.current &&
      isAuthenticated
    ) {
      reloadEventsBasedOnUserRole(eventFilter);
      prevIsAuthenticatedRef.current = isAuthenticated;
    }
  }, [APP_VARIANT, eventFilter, isAuthenticated, reloadEventsBasedOnUserRole]);

  useEffect(() => {
    const openEventDialog = async () => {
      if (eventId) {
        const event = await getEventById(eventId);
        if (APP_VARIANT === 'sjone') {
          if (
            !isOneOfUserCompaniesDocument(event, userInfos) &&
            !isPublicCompanyDocument(event, currentCompanyId, isPublicCompany)
          ) {
            navigate('/403');
          }
        }
        if (event) {
          setSelectedEvent(event);
          setDialogOpen(true);
        }
      }
    };
    openEventDialog();
  }, [
    APP_VARIANT,
    eventId,
    navigate,
    userInfos,
    eventFilter,
    currentCompanyId,
    isPublicCompany
  ]);

  useEffect(() => {
    if (locationSet && prevLocationSetRef.current !== locationSet) {
      reloadEventsBasedOnUserRole(eventFilter);
    }
    prevLocationSetRef.current = locationSet;
  }, [eventFilter, locationSet, reloadEventsBasedOnUserRole]);

  return (
    <>
      <Grid container marginTop={3}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: {xs: 'center', sm: 'flex-end'},
            width: '100%',
            marginBottom: {xs: 1, sm: 0}
          }}
        >
          {isUserHealthManager && (
            <StateSwitch
              value={showCompanyEvents}
              onChange={handleShowCompanyEventsChange}
              labelOn={'show_company_events'}
              labelOff={'show_all_events'}
            />
          )}
          <RefreshButton
            label={'reload_events'}
            onClick={() =>
              showCompanyEvents
                ? reloadCompanyEvents(eventFilter)
                : reloadEventsBasedOnUserRole(eventFilter)
            }
          />

          {(globalFilterChanged || filterVisible) && (
            <RemoveFilterButton
              label={'reset_filter'}
              onClick={handleResetFilter}
            />
          )}
        </Box>
        <>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: {xs: 'column', sm: 'row'},
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: {xs: 0, sm: 2}
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  marginTop: {
                    xs: 0,
                    sm: activeFilters.length > 0 && !filterVisible ? 0 : 1.5
                  },
                  marginBottom: {
                    xs: 0,
                    sm: activeFilters.length > 0 && !filterVisible ? 4.5 : 0
                  },
                  order: {xs: 1, sm: 2}
                }}
              >
                <FilterButton
                  onClick={handleToggleFilterVisibility}
                  documentType={'events'}
                  filterVisible={filterVisible}
                />
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  order: {xs: 2, sm: 1},
                  width: {xs: '100%', sm: 'auto'}
                }}
              >
                <TextSearch
                  searchType={onDemand ? 'onDemandEvent' : 'event'}
                  searchLabel="event_search"
                  onApply={handleApplyFilter}
                />
                {!filterVisible && activeFilters.length > 0 && (
                  <Box marginTop={2} display="flex" flexWrap="wrap" gap={1}>
                    {activeFilters.map((filter) => (
                      <FilterChip
                        key={filter.value}
                        filter={filter}
                        onRemove={handleRemoveFilter}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
            {filterVisible && (
              <Box mt={1}>
                <FilterForm
                  key={filterResetKey}
                  filterType={onDemand ? 'onDemandEvent' : 'event'}
                  onApply={(newFilterData, hideFilter) =>
                    handleApplyFilter(hideFilter, newFilterData)
                  }
                  hideFilterOnApply={true}
                />
              </Box>
            )}
          </Grid>
        </>
      </Grid>

      <InfiniteScroll
        dataLength={events.length}
        next={fetchNextPageCallback}
        hasMore={hasMore}
        loader={<LoadingScreen />}
        endMessage={
          <Typography
            variant="body1"
            align="center"
            marginTop={2}
            paddingBottom={2}
          >
            <b>
              {onDemand ? t('saw_all_events_on_demand') : t('saw_all_events')}
            </b>
          </Typography>
        }
      >
        <Grid
          container
          rowSpacing={5}
          columnSpacing={4}
          sx={{
            marginTop: 3
          }}
          maxWidth={'lg'}
        >
          {events.map((event, index) => {
            return (
              <MatrixUnreadEventsProvider documentId={event._id} key={index}>
                <EventCard
                  key={index}
                  event={event}
                  onEventCardClick={onEventCardClick}
                  cardMediaHeight="150"
                  alwaysTruncate={true}
                />
              </MatrixUnreadEventsProvider>
            );
          })}
        </Grid>

        {selectedEvent && (
          <MatrixUnreadEventsProvider documentId={selectedEvent._id}>
            <MatrixRoomProvider childId={selectedEvent._id}>
              <EventDetailsDialog
                open={dialogOpen}
                onClose={handleCloseEventDetails}
                event={selectedEvent}
                onChange={onEventChanged}
                onDelete={onEventDeleted}
                onClone={reloadEventsBasedOnUserRole}
                groupOfEventChanged={handleGroupOfEventChanged}
                groupId={groupId}
                onGroupClose={onGroupClose}
              />
            </MatrixRoomProvider>
          </MatrixUnreadEventsProvider>
        )}
        {(isAuthenticated && !onDemand) || (isAdmin && onDemand) ? (
          <Grid container justifyContent="flex-end" padding={2}>
            <ResponsiveFab onClick={handleFabClick} title={t('new_event')} />
          </Grid>
        ) : null}

        <DialogForm
          open={showCreateForm}
          onSubmit={onEventCreated}
          onCancel={handleEventCreationCancel}
          onError={onEventCreationError}
          FormComponent={EventForm}
          mode={EventFormMode.CREATE}
          onDemand={onDemand}
          maxHeightAllowed={'1200px'}
        />
      </InfiniteScroll>
    </>
  );
};

export default EventView;
